<script setup>
defineOptions({ inheritAttrs: false });

//#region ###############   IMPORTS   ###############
import VideoCallButton from "@/components/Common/VideoCallButton.vue";
import i18n from "@/i18n";
import { useVideoCallState } from "@/stores/videoCallState";
import { useLicenseState } from "@/stores/licenseState";
import { Actions, ChangeSectionPayload, EventManager, Events } from "@events";
import { SectionList } from "@module-system";
import axios from "axios";
import { computed, onMounted, onUnmounted, ref } from "vue";
import photoRater from "../helpers/photoRater";
import { PhotoPanelActions, PhotoPanelEvents } from "../helpers/photoPanelActions";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["setPanelSize"]);

//#endregion

//#region ###############   DATA   ###############
// video element contents are drawn to this canvas
const takePhotoCanvas = ref(null);

const loading = ref(false);

// holds taken photos as base64 strings
const imageUrisForSelection = ref([]);

// selected image uri from imageUrisForSelection
const selectedImage = ref(null);

// when set to true, snackbar is shown for 2 seconds with given text and color
const snackbar = ref(false);
const snackBarText = ref("");
const snackBarColor = ref("");

const state = useVideoCallState(props.rtcId);
const actionEmitter = EventManager.getActionEmitter(props.rtcId);
const eventEmitter = EventManager.getEventEmitter(props.rtcId);
const selectedDocumentType = ref(null);
const selectedDocumentFace = ref(null);

eventEmitter.on(Events.TextChannelMessageReceived, ({ data, channel }) => {
  if (data.startsWith("CAMERASTATUS|")) {
    const parts = data.split("|");
    const cameraStatus = parts[1];
    const remotePeer = state.peers.find((x) => x.type == "remote");

    state.setPeerCamera(remotePeer.peerName, cameraStatus);
    eventEmitter.emit(Events.CameraChanged, {
      cameraStatus,
      peerName: remotePeer.peerName,
    });
  }
});

eventEmitter.on(PhotoPanelEvents.PhotoPanelTakePhotoOn, ({ documentTypeFace }) => {
  selectedDocumentFace.value = documentTypeFace;
  selectedDocumentType.value = documentTypeFace.documentType;
});

actionEmitter.on(PhotoPanelActions.TakePhoto, () => {
  takePhoto();
});

actionEmitter.on(PhotoPanelActions.ClosePhotoPanel, () => {
  closePanel();
});
// #endregion

//#region ###############   UTILITY FUNCTIONS PANEL, SNACKBAR   ###############
function showSnackbar(text, color = "") {
  snackbar.value = true;
  snackBarText.value = text;
  snackBarColor.value = color;
}

function clearState(keepDocType = false) {
  imageUrisForSelection.value = [];
  hoveredImage.value = null;

  selectedDocumentFace.value = null;

  if (!keepDocType) {
    selectedDocumentType.value = null;
  }

  selectedImage.value = null;
}

function closePanel(keepDocType = false) {
  clearState(keepDocType);

  emit("setPanelSize", null);

  actionEmitter.emit(
    Actions.ChangeSectionModule,
    new ChangeSectionPayload({
      section: SectionList.CallBottomPanel,
      key: "photo-document-selection",
      state: false,
    }),
  );
}

function triggerClosePanel() {
  actionEmitter.emit(PhotoPanelActions.ClosePhotoPanel);
}
//#endregion

//#region ###############   document photo take, selection and uplaod logic    ###############

// takes photo from video element and draws it to canvas 5 times with 200 ms interval
// adds taken photos to imageUrisForSelection
// shows flash white effect
async function takePhoto() {
  // if no document type or face is selected, return

  if (selectedDocumentType.value == null || !selectedDocumentFace.value == null) return;

  const context = takePhotoCanvas.value.getContext("2d", { willReadFrequently: true });
  const video = state.peers.find((x) => x.type == "remote")?.video;

  takePhotoCanvas.value.width = video.videoWidth;
  takePhotoCanvas.value.height = video.videoHeight;

  eventEmitter.emit(PhotoPanelEvents.PhotoTaken);

  let maxPhotoRating = 0;
  let maxPhotoRatingIndex = 0;

  // take photo 5 times with 500 ms interval

  for (let i = 0; i < 5; i++) {
    const flash = document.createElement("div");
    flash.classList.add("flash-white-effect");
    document.body.appendChild(flash);

    setTimeout(() => {
      flash.remove();
    }, 200);

    context.drawImage(video, 0, 0, takePhotoCanvas.value.width, takePhotoCanvas.value.height);

    // flash white effect

    const data = takePhotoCanvas.value.toDataURL("image/jpeg");

    // Imported Rating Method from ECV 1.5
    const rating = photoRater(context, takePhotoCanvas.value);
    console.log("Photo rated: ", { index: i, rating });

    if (rating > maxPhotoRating) {
      maxPhotoRating = rating;
      maxPhotoRatingIndex = i;
    }

    imageUrisForSelection.value.push({ index: i, uri: data });

    // Auto selecting photo based on rating
    if (i >= 4) {
      selectImage(imageUrisForSelection.value[maxPhotoRatingIndex]);
      console.log("Best rated photo has been auto selected: ", { maxPhotoRatingIndex, maxPhotoRating });
    } else {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  actionEmitter.emit(
    Actions.ChangeSectionModule,
    new ChangeSectionPayload({
      section: SectionList.CallBottomPanel,
      key: "photo-document-selection",
    }),
  );

  emit("setPanelSize", 200);
}

function isSelectedImage(img) {
  return selectedImage.value?.index == img.index;
}

// image clicked in preview
function selectImage(img) {
  selectedImage.value = img;
}

const hoveredImage = ref(null);
const mouseLeftPx = ref(0);
const hoverImageRef = ref(null);
function onHoverImage(event, image) {
  hoveredImage.value = image;
  mouseLeftPx.value = event ? event.target.getBoundingClientRect().left : 0;
  // if left + image width > window width, set left to window width - image width - 20px
  if (hoverImageRef.value) {
    const hoverImageWidth = hoverImageRef.value.getBoundingClientRect().width;
    if (mouseLeftPx.value + hoverImageWidth > window.innerWidth) {
      mouseLeftPx.value = window.innerWidth - hoverImageWidth - 20;
    }
  }

  // if left < 0, set left to 20px
  if (mouseLeftPx.value < 0) {
    mouseLeftPx.value = 20;
  }
}

const getBase64StringFromDataURL = (dataURL) => dataURL.replace("data:", "").replace(/^.+,/, "");

// sends selected image to server
// if success, adds sent document type and face to sentDocumentTypeFaces
// shows snackbar
// closes panel
// if error, shows snackbar
// closes panel
async function sendImage() {
  if (!selectedImage.value) return;
  try {
    console.log("selectedDocumentType.value", selectedDocumentType.value);
    console.log("selectedDocumentFace.value", selectedDocumentFace.value);
    loading.value = true;
    var res = await axios.post("/api/call/photo", {
      rtcId: props.rtcId,
      documentTypeId: selectedDocumentType.value.id,
      documentTypeFaceId: selectedDocumentFace.value.id,
      data: getBase64StringFromDataURL(selectedImage.value.uri),
    });

    if (res.data.success) {
      eventEmitter.emit(Events.PhotoTaken, {
        rtcId: props.rtcId,
        documentTypeId: selectedDocumentType.value.id,
        documentTypeFaceId: selectedDocumentFace.value.id,
      });

      eventEmitter.emit(PhotoPanelEvents.PhotoUploaded, {
        rtcId: props.rtcId,
        documentTypeId: selectedDocumentType.value.id,
        documentTypeFaceId: selectedDocumentFace.value.id,
      });

      showSnackbar(i18n.global.t("PhotoSentSuccessfully"), "green");
    } else {
      console.log("Error sending photo", res.data);
      showSnackbar(i18n.global.t("ErrorSendingPhoto"), "red");
    }
  } catch (e) {
    console.log("Error sending photo", e);
    showSnackbar(i18n.global.t("ErrorSendingPhoto"), "red");
  } finally {
    loading.value = false;
    closePanel(true);
    actionEmitter.emit(Actions.PhotoPanelUpdate);
    actionEmitter.emit(PhotoPanelActions.OpenTypeSelectionPanel);
    actionEmitter.emit(PhotoPanelActions.ClearDocFaceSelection);

  }
}

//#endregion

// auto photo for system document
const autoPhotoTaken = ref(false);
async function autoPhoto() {
  const licenseState = useLicenseState();

  if (!licenseState.hasLicensePackage("AutoPhoto")) {
    console.warn("AutoPhoto license not found");
    return;
  }

  if (!state?.callData?.queue?.autoPhotoEnabled ?? false) {
    console.log("Auto photo not enabled for this call");
    return;
  }

  setTimeout(async () => {
    if (autoPhotoTaken.value) {
      console.log("Auto photo already taken, skipping");
      return;
    }

    autoPhotoTaken.value = true;
    const context = takePhotoCanvas.value.getContext("2d", { willReadFrequently: true });
    const video = state.peers.find((x) => x.type == "remote")?.video;

    takePhotoCanvas.value.width = video.videoWidth;
    takePhotoCanvas.value.height = video.videoHeight;

    context.drawImage(video, 0, 0, takePhotoCanvas.value.width, takePhotoCanvas.value.height);

    const data = takePhotoCanvas.value.toDataURL("image/jpeg");

    const res = await axios.post("/api/call/photo", {
      rtcId: state.callData.rtcId,
      documentTypeId: "SYSTEM",
      documentTypeFaceId: "F",
      data: getBase64StringFromDataURL(data),
    });

    if (res.data.success) {
      console.log("Auto photo sent successfully");
    } else {
      console.log("Error sending auto photo", res.data);
    }
  }, state?.callData?.queue?.autoPhotoDelayMS ?? 5000);
}

onMounted(() => {
  console.log("PhotoDocumentSelection mounted, waiting for call data and webrtc connection to auto take photo if enabled");
  Promise.all([state.callDataLoadedPromise, state.webrtcConnectedPromise]).then(() => {
    console.log("Call data and webrtc connection ready, checking for auto photo");
    autoPhoto();
  });
});
</script>

<template>
  <v-container :fluid="true" class="document-selection-container" v-if="state.callData && !loading">
    <canvas ref="takePhotoCanvas" class="takePhotoCanvas"></canvas>
    <v-row style="justify-content: center; overflow-x: auto; flex-wrap: nowrap">
      <div class="preview-image-container" v-for="(image, index) in imageUrisForSelection" :key="index">
        <img
          draggable="false"
          :src="image.uri"
          @mouseenter="(e) => onHoverImage(e, image)"
          @mousemove="(e) => onHoverImage(e, image)"
          @mouseleave="hoveredImage = null"
          :class="'preview-image ' + (isSelectedImage(image) ? 'selected' : '')"
          @click="selectImage(image)" />
        <v-icon class="selected-image-check" v-if="isSelectedImage(image)">mdi-check</v-icon>
      </div>
      <v-col cols="1" class="preview-buttons-container">
        <VideoCallButton
          style="background-color: rgb(221, 44, 0); border-color: rgb(221, 44, 0)"
          icon="mdi-close"
          @click="triggerClosePanel">
        </VideoCallButton>

        <VideoCallButton
          :class="'photo-accept-button ' + (selectedImage ? '' : 'disabled')"
          icon="mdi-check"
          @click="sendImage"></VideoCallButton>
      </v-col>
    </v-row>
  </v-container>

  <v-row v-else style="height: 100%">
    <v-col style="height: 100%; display: flex; justify-content: center; align-items: center">
      <v-progress-circular :size="100" :width="20" indeterminate color="purple"></v-progress-circular>
    </v-col>
  </v-row>
  <v-snackbar location="top" :color="snackBarColor" v-model="snackbar" :timeout="2000">
    {{ snackBarText }}

    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar = false">
        {{ $t("Close") }}
      </v-btn>
    </template>
  </v-snackbar>

  <!-- hover content -->
  <div v-if="hoveredImage" class="hover-content" :style="'left:' + mouseLeftPx + 'px' + '; bottom:200px'" ref="hoverImageRef">
    <img draggable="false" :src="hoveredImage.uri" />
  </div>
</template>

<style>
.document-selection-container {
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
}

.takePhotoCanvas {
  width: 100%;
  height: 100%;
  object-fit: contain;
  position: fixed;
  visibility: collapse;
  z-index: -1;
  left: 0;
  top: 0;
  pointer-events: none;
}

.preview-image-container {
  position: relative;
  height: 150px;
  margin: 3px;
  display: flex;
}

.preview-image {
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
  border-radius: 10px;
  cursor: pointer;
}

.preview-image {
  border: 2px solid rgb(131 131 131);
}

.preview-image:hover {
  border: 2px solid rgb(207, 207, 207);
}

.preview-image.selected {
  border: 4px solid rgb(0 128 0);
}

.selected-image-check {
  position: absolute;
  color: limegreen;
  font-size: 60px;
  top: 0;
  right: 0;
  text-shadow: -3px 0 white, 0 3px white, 3px 0 white, 0 -3px white;
  pointer-events: none;
}

.flash-white-effect {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  animation: flash 0.2s;
  pointer-events: none;
}

@keyframes flash {
  0% {
    opacity: 1;
  }

  99% {
    opacity: 0;
  }

  100% {
    display: none;
    opacity: 0;
  }
}

.preview-buttons-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.ecv-call-button.photo-accept-button {
  background-color: rgb(0, 221, 0);
}

.ecv-call-button.photo-accept-button:hover {
  background-color: rgb(0, 221, 0);
}

.ecv-call-button.take-photo-button.disabled {
  background-color: rgb(221 221 221 / 50%);
}

.ecv-call-button.take-photo-button.disabled:hover {
  background-color: rgb(221 221 221 / 50%);
}

.ecv-call-button.take-photo-button {
  background-color: rgba(10, 180, 61, 0.5);
}

.ecv-call-button.take-photo-button:hover {
  background-color: rgba(15, 238, 82, 0.7);
}

option.disabled {
  /* slight green background */
  color: rgb(37 37 37 / 50%) !important;
  background: rgb(173 255 180 / 50%) !important;
  text-decoration: line-through;
}

option.disabled:checked {
  background: #000 !important;
}
</style>
